import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { SettingsApiResponse } from '../settings-form/settings-config.interface';
import { SettingKey, SettingScope } from '../settings-form/setting-key.model';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * Minimal user shape bab-user-view needs - every app's own User/AppUser
 * interface already has at least this much (see company's User,
 * shared-auth's AppUser).
 */
export interface ProfileUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  lang?: string;
  isActive?: boolean;
  isVerified?: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  role?: { name: string } | string | null;
  companyId?: number | null;
  company?: { name: string } | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export type ProfileFieldUpdate = Partial<Pick<ProfileUser, 'firstName' | 'lastName' | 'phone' | 'lang'>>;

/**
 * General + Settings + Documents only (see shared user-view plan) -
 * Transfer/Wallet get their own tokens in a later phase. Every function is
 * optional except the two self-view ones; a function's absence hides the
 * UI it would power (same opt-in shape as DocumentsTableConfig's
 * approve/reject/setPending).
 */
export interface UserProfileConfig {
  /** Self mode (no subjectId) - "my own profile". */
  getSelf: () => Observable<ApiResponse<ProfileUser>>;
  updateSelf: (data: ProfileFieldUpdate) => Observable<ApiResponse<ProfileUser>>;

  /** Admin mode (subjectId given) - viewing/editing someone else's profile. */
  getById?: (id: number) => Observable<ApiResponse<ProfileUser>>;
  updateById?: (id: number, data: ProfileFieldUpdate & { isActive?: boolean }) => Observable<ApiResponse<ProfileUser>>;
  deleteById?: (id: number) => Observable<unknown>;
  changePassword?: (id: number, newPassword: string) => Observable<unknown>;
  sendPasswordResetEmail?: (id: number) => Observable<unknown>;

  /**
   * Settings tab - same split (values vs keys/metadata) SettingsFormConfig
   * already expects, but both take entityId: some backends (company's
   * CompanyUserSettingsService) have no standalone "keys only" endpoint -
   * only a per-user merged one - unlike the generic SettingsFormConfig
   * used elsewhere (company-wide keys, no per-entity variation).
   */
  settingsScope?: SettingScope;
  getSettings?: (entityId: number) => Observable<SettingsApiResponse<Record<string, string>>>;
  getSettingKeys?: (entityId: number) => Observable<SettingsApiResponse<SettingKey[]>>;
  updateSettings?: (entityId: number, settings: Array<{ key: string; value: string }>) => Observable<SettingsApiResponse<Record<string, string>>>;

  /** Documents tab - omit to hide it entirely. */
  showDocuments?: boolean;

  /**
   * General tab fields with no backing column for this app's user table
   * (e.g. admin_users has only a single `name` column - no phone/lang at
   * all) - hidden rather than shown-but-silently-not-persisted.
   */
  hiddenFields?: Array<'phone' | 'lang'>;
}

export const USER_PROFILE_CONFIG = new InjectionToken<UserProfileConfig>('USER_PROFILE_CONFIG');
